/* ***************************************************************** */
/*                                                                   */
/* Licensed Materials - Property of IBM                              */
/*                                                                   */
/* (C) Copyright IBM Corp. 2023                                      */
/*                                                                   */
/* ***************************************************************** */

import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { addHighlightedToItems} from "./home-page-utils";
import { get } from '../../utils';
import { SUGGESTIONS_HOME_PAGE_NAME } from "../../components/response-loading/redux/response-loading-redux";
import {UNEXPECTED_ERROR_MESSAGE} from "../../components/common/constants";
import {compareStartsWithOrContains} from "../../utils/sort-search-utils";

export const SuggestionsPullDown = ({
    props,
    items = [],
    isDirectSearchResponseLoading,
    setDirectSearchNoResultsFound,
    setNoResultsMatchEntry,
    dispatch,
    makeDirectSearchActionThunk,
    noSuggestionsFound,
    searchValue = "",
    setShouldHidePullDown,
    setMultipleResults,
    inputGlobalSearchFieldRef,
    isPullDownItemActive,
    setDidYouMeanResult,
    abortControllerDirectSearchRef,
    shouldShowErrorMessageSuggestion
}) => {
    const topDrugsResponse = useSelector((state) => get(state, "home_page.adobe_analytics_top_drugs", {}))
    const suggestionsLoading = useSelector((state) => get(state, `inline_loading.${SUGGESTIONS_HOME_PAGE_NAME}`, {}));
    const topDrugs = topDrugsResponse?.rows?.map(item => item.value);
    const pullDownItemRef = useRef(null);
    const scrollContainerRef = useRef(null);
    const [activeRow, setActiveRow] = useState(0);
    const [menuItems, setMenuItems] = useState([]);

    const MAX_MENU_ITEM_LENGTH = 30;
    const filteredSuggestions = [...new Set(items)]
        .filter(item => (item.drugName || item).toUpperCase().includes(searchValue.toUpperCase()))
        .sort((a, b) => compareStartsWithOrContains(a?.drugName, b?.drugName, searchValue))
        .slice(0, MAX_MENU_ITEM_LENGTH);
    let content = [];

    useEffect(() => {
        setTimeout(() => {
            if (!scrollContainerRef.current) return;
            const items = Array.from(scrollContainerRef.current.querySelectorAll('.suggestions-pull-down-menu-item'));
            setMenuItems(items);
        }, 0);
    }, [filteredSuggestions, topDrugs, noSuggestionsFound, shouldShowErrorMessageSuggestion]);

    useEffect(() => {
        if (!isPullDownItemActive) {
            return;
        }

        const item = menuItems[activeRow];
        if (!item) return;

        item.focus({ preventScroll: true });

        const container = scrollContainerRef.current;
        if (!container) return;

        const containerTop = container.scrollTop;
        const containerBottom = containerTop + container.clientHeight;

        const itemTop = item.offsetTop;
        const itemBottom = itemTop + item.offsetHeight;

        if (itemTop < containerTop) {
            container.scrollTop = itemTop;
        } else if (itemBottom > containerBottom) {
            container.scrollTop = itemBottom - container.clientHeight;
        }
    }, [isPullDownItemActive, activeRow, menuItems]);

    const onKeyDownHandler = (e) => {
        if (e.key === "ArrowUp") {
            e.preventDefault();
            const numberOfItems = content.length;
            if (numberOfItems === 0) return;
            setActiveRow((activeRow + numberOfItems - 1) % numberOfItems);
        }
        if (e.key === "ArrowDown") {
            e.preventDefault();
            const numberOfItems = content.length;
            if (numberOfItems === 0) return;
            setActiveRow((activeRow + 1) % numberOfItems);
        }
    }

    const pullDownItemEventsHandler = (item) => {
        const signal = abortControllerDirectSearchRef.current.signal;
        const isSearchFromPullDown = true;
        setShouldHidePullDown(true);
        dispatch(makeDirectSearchActionThunk(
            props, 
            item.drugName || item,
            setDirectSearchNoResultsFound,
            setNoResultsMatchEntry, 
            setMultipleResults,
            inputGlobalSearchFieldRef,
            setDidYouMeanResult,
            searchValue,
            isSearchFromPullDown,
            signal,
            item.quickDocumentId
        ));
    }

    function fillContentList(itemsList, eventHandler) {
        content = itemsList?.map((item, index) => {
            const drugName = item.drugName || item;
            const highlightedItem = addHighlightedToItems(drugName, searchValue);
            return (
                <div
                    className="suggestions-pull-down-menu-item"
                    key={drugName}
                    tabIndex={0}
                    ref={index === activeRow ? pullDownItemRef : null}
                    onClick={() => {
                        if (!isDirectSearchResponseLoading) {
                            eventHandler(item);
                        }
                    }}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && !isDirectSearchResponseLoading) {
                            eventHandler(item);
                        }
                    }}
                    onMouseDown={(e) => e.preventDefault()}
                >
                    <p className="suggestions-pull-down-menu-item-text" title={drugName}>{highlightedItem}</p>
                </div>
            );
        });
    }

    if (suggestionsLoading.status === "active") {
        content.push(<div></div>);
    } else if ((noSuggestionsFound || filteredSuggestions.length === 0) && searchValue.length !== 0  && !shouldShowErrorMessageSuggestion) {
        content.push(<div key="no-results" className="picklist-pulldown-no-results">No matching entry.</div>);
    } else if (shouldShowErrorMessageSuggestion) {
        content.push(<div key="no-results" className="picklist-pulldown-no-results">{UNEXPECTED_ERROR_MESSAGE}</div>)
    } else if (searchValue.length === 0) {
        fillContentList(topDrugs, pullDownItemEventsHandler);
    } else {
        fillContentList(filteredSuggestions, pullDownItemEventsHandler);
    }

    return <div className="picklist-pulldown-menu-container global-search-pull-down-container">
                <div className="picklist-pulldown-menu-open global-search-pull-down">
                    <div
                        className="suggestions-pull-down-menu-scroll"
                        onKeyDown={onKeyDownHandler}
                        ref={scrollContainerRef}
                    >
                        {content}
                    </div>
                </div>
            </div>   
}
